import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Mood-adaptive color palettes that subtly shift based on how the user feels.
// Two variants: one tuned for dark themes, one AA-checked for light themes.
const MOOD_OVERLAYS = {
  1: { // Struggling - warm, gentle, comforting
    '--accent': '#D4916E',
    '--accent-hover': '#E0A88A',
    '--accent-soft': 'rgba(212,145,110,0.12)',
    '--accent-secondary': '#8B6F5C',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(212,145,110,0.06) 0%, transparent 60%)',
    '--mood-label': 'We see you. Take it slow.',
  },
  2: { // Low - soft amber, nurturing
    '--accent': '#CDA070',
    '--accent-hover': '#DEBB8E',
    '--accent-soft': 'rgba(205,160,112,0.10)',
    '--accent-secondary': '#7A6E5A',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(205,160,112,0.05) 0%, transparent 60%)',
    '--mood-label': 'Gentle steps forward.',
  },
  3: { // Okay - balanced, neutral (base theme)
    '--mood-glow': 'none',
    '--mood-label': '',
  },
  4: { // Good - fresh greens, uplifting
    '--accent': '#7BA882',
    '--accent-hover': '#92BF98',
    '--accent-soft': 'rgba(123,168,130,0.10)',
    '--accent-secondary': '#5A8A6E',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(123,168,130,0.05) 0%, transparent 60%)',
    '--mood-label': 'Looking good today.',
  },
  5: { // Thriving - vibrant teal, energizing
    '--accent': '#5EAAB0',
    '--accent-hover': '#78C4CA',
    '--accent-soft': 'rgba(94,170,176,0.10)',
    '--accent-secondary': '#4A8A8E',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(94,170,176,0.06) 0%, transparent 60%)',
    '--mood-label': 'You\'re radiating today.',
  },
};

// Deeper mood accents for light themes (WCAG AA with white button text).
const MOOD_OVERLAYS_LIGHT = {
  1: {
    '--accent': '#A34A3E',
    '--accent-hover': '#8D3E33',
    '--accent-soft': 'rgba(163,74,62,0.10)',
    '--accent-secondary': '#8B6F5C',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(163,74,62,0.05) 0%, transparent 60%)',
    '--mood-label': 'We see you. Take it slow.',
  },
  2: {
    '--accent': '#A66A2B',
    '--accent-hover': '#8F5A22',
    '--accent-soft': 'rgba(166,106,43,0.10)',
    '--accent-secondary': '#7A6E5A',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(166,106,43,0.05) 0%, transparent 60%)',
    '--mood-label': 'Gentle steps forward.',
  },
  3: {
    '--mood-glow': 'none',
    '--mood-label': '',
  },
  4: {
    '--accent': '#537858',
    '--accent-hover': '#456549',
    '--accent-soft': 'rgba(83,120,88,0.10)',
    '--accent-secondary': '#5A8A6E',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(83,120,88,0.05) 0%, transparent 60%)',
    '--mood-label': 'Looking good today.',
  },
  5: {
    '--accent': '#367172',
    '--accent-hover': '#2C5D5E',
    '--accent-soft': 'rgba(54,113,114,0.10)',
    '--accent-secondary': '#4A8A8E',
    '--mood-glow': 'radial-gradient(ellipse at 50% 0%, rgba(54,113,114,0.06) 0%, transparent 60%)',
    '--mood-label': 'You\'re radiating today.',
  },
};

const THEMES = {
  'soft-light': {
    name: 'Soft Light',
    label: 'Light & Open',
    light: true,
    vars: {
      '--bg-primary': '#FAF5EC',
      '--bg-surface': '#FFFFFF',
      '--bg-surface-hover': '#F3EDE1',
      '--bg-subtle': '#F1EADC',
      '--bg-input': '#F5EFE4',
      '--accent': '#825743',
      '--accent-hover': '#6E4938',
      '--accent-soft': 'rgba(130,87,67,0.10)',
      '--accent-secondary': '#6B8E70',
      '--text-primary': '#2D2A26',
      '--text-secondary': '#5B5651',
      '--border': '#E7DFD1',
      '--border-hover': '#D6CBB8',
      '--shadow-sm': '0 2px 10px rgba(130,87,67,0.04)',
      '--shadow-md': '0 4px 20px rgba(130,87,67,0.06)',
      '--shadow-lg': '0 8px 32px rgba(45,42,38,0.08)',
    },
  },
  'warm-earth': {
    name: 'Warm Earth',
    label: 'Earthy & Grounded',
    vars: {
      '--bg-primary': '#1A1510',
      '--bg-surface': '#231E17',
      '--bg-surface-hover': '#2C2519',
      '--bg-subtle': 'rgba(255,235,205,0.03)',
      '--bg-input': 'rgba(255,235,205,0.05)',
      '--accent': '#D4915E',
      '--accent-hover': '#E3A874',
      '--accent-soft': 'rgba(212,145,94,0.12)',
      '--accent-secondary': '#6B7F5E',
      '--text-primary': '#F5EDE3',
      '--text-secondary': '#B5A898',
      '--border': 'rgba(255,235,205,0.08)',
      '--border-hover': 'rgba(255,235,205,0.14)',
      '--shadow-sm': '0 2px 10px rgba(0,0,0,0.25)',
      '--shadow-md': '0 4px 20px rgba(0,0,0,0.3)',
      '--shadow-lg': '0 8px 32px rgba(0,0,0,0.4)',
    },
  },
  'dark-void': {
    name: 'Dark Void',
    label: 'Deep & Intimate',
    vars: {
      '--bg-primary': '#050505',
      '--bg-surface': '#121212',
      '--bg-surface-hover': '#1A1A1A',
      '--bg-subtle': 'rgba(255,255,255,0.02)',
      '--bg-input': 'rgba(255,255,255,0.04)',
      '--accent': '#C08261',
      '--accent-hover': '#D99775',
      '--accent-soft': 'rgba(192,130,97,0.1)',
      '--accent-secondary': '#4A5D4E',
      '--text-primary': '#F4F4F0',
      '--text-secondary': '#A1A1AA',
      '--border': 'rgba(255,255,255,0.08)',
      '--border-hover': 'rgba(255,255,255,0.12)',
      '--shadow-sm': '0 2px 10px rgba(0,0,0,0.25)',
      '--shadow-md': '0 4px 20px rgba(0,0,0,0.3)',
      '--shadow-lg': '0 8px 32px rgba(0,0,0,0.4)',
    },
  },
  'ocean-depth': {
    name: 'Ocean Depth',
    label: 'Deep & Calming',
    vars: {
      '--bg-primary': '#070B10',
      '--bg-surface': '#0E1520',
      '--bg-surface-hover': '#151E2C',
      '--bg-subtle': 'rgba(100,160,220,0.03)',
      '--bg-input': 'rgba(100,160,220,0.05)',
      '--accent': '#5E9EC0',
      '--accent-hover': '#78B4D4',
      '--accent-soft': 'rgba(94,158,192,0.1)',
      '--accent-secondary': '#4A6E5D',
      '--text-primary': '#E8EDF2',
      '--text-secondary': '#8899AA',
      '--border': 'rgba(100,160,220,0.08)',
      '--border-hover': 'rgba(100,160,220,0.14)',
      '--shadow-sm': '0 2px 10px rgba(0,0,0,0.25)',
      '--shadow-md': '0 4px 20px rgba(0,0,0,0.3)',
      '--shadow-lg': '0 8px 32px rgba(0,0,0,0.4)',
    },
  },
};

const ThemeContext = createContext(null);

const isSunsetHour = () => {
  const h = new Date().getHours();
  return h >= 20 || h < 6;
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // One-time migration: the platform default moved from dark-void to soft-light.
    // Users who explicitly re-pick a dark theme afterwards keep their choice.
    if (!localStorage.getItem('innerscope-theme-v2')) {
      localStorage.setItem('innerscope-theme-v2', '1');
      localStorage.setItem('innerscope-theme', 'soft-light');
      return 'soft-light';
    }
    const saved = localStorage.getItem('innerscope-theme');
    return THEMES[saved] ? saved : 'soft-light';
  });
  const [currentMood, setCurrentMood] = useState(() => {
    const saved = localStorage.getItem('innerscope-mood');
    return saved ? parseInt(saved) : 3;
  });
  const [calmMode, setCalmMode] = useState(() => {
    const saved = localStorage.getItem('innerscope-calm');
    return saved === 'true' || saved === 'auto';
  });
  const [calmModeManual, setCalmModeManual] = useState(() => {
    return localStorage.getItem('innerscope-calm') === 'true';
  });
  // Sunset mode: after 8pm (until 6am) the light theme softens into Warm Earth.
  const [sunsetMode, setSunsetModeState] = useState(() => localStorage.getItem('innerscope-sunset') !== 'false');
  const [sunsetActive, setSunsetActive] = useState(isSunsetHour());

  useEffect(() => {
    const id = setInterval(() => setSunsetActive(isSunsetHour()), 60000);
    return () => clearInterval(id);
  }, []);

  const effectiveTheme = (sunsetMode && sunsetActive && theme === 'soft-light') ? 'warm-earth' : theme;

  const applyTheme = useCallback(() => {
    const t = THEMES[effectiveTheme];
    if (!t) return;
    const root = document.documentElement;
    
    // Apply base theme
    Object.entries(t.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Apply mood overlay (only accent colors, not bg) — light themes use
    // deeper accents so white button text stays WCAG AA.
    const overlaySet = t.light ? MOOD_OVERLAYS_LIGHT : MOOD_OVERLAYS;
    const moodOverlay = overlaySet[currentMood];
    if (moodOverlay && currentMood !== 3) {
      Object.entries(moodOverlay).forEach(([key, value]) => {
        if (key.startsWith('--accent') || key.startsWith('--mood')) {
          root.style.setProperty(key, value);
        }
      });
    } else {
      root.style.setProperty('--mood-glow', 'none');
      root.style.setProperty('--mood-label', '');
    }
    
    localStorage.setItem('innerscope-theme', theme);
  }, [theme, effectiveTheme, currentMood]);

  useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  // Auto-trigger calm mode on low mood (1-2), unless manually toggled off
  useEffect(() => {
    if (calmModeManual) {
      setCalmMode(true);
    } else if (currentMood <= 2 && currentMood >= 1) {
      setCalmMode(true);
      localStorage.setItem('innerscope-calm', 'auto');
    } else {
      setCalmMode(false);
      localStorage.setItem('innerscope-calm', 'false');
    }
  }, [currentMood, calmModeManual]);

  const setMood = (mood) => {
    setCurrentMood(mood);
    localStorage.setItem('innerscope-mood', mood.toString());
  };

  const toggleCalmMode = (manual) => {
    if (manual !== undefined) {
      setCalmModeManual(manual);
      setCalmMode(manual);
      localStorage.setItem('innerscope-calm', manual ? 'true' : 'false');
    } else {
      const next = !calmMode;
      setCalmModeManual(next);
      setCalmMode(next);
      localStorage.setItem('innerscope-calm', next ? 'true' : 'false');
    }
  };

  const setSunsetMode = (on) => {
    setSunsetModeState(on);
    localStorage.setItem('innerscope-sunset', on ? 'true' : 'false');
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES, effectiveTheme, sunsetMode, setSunsetMode, sunsetActive, currentMood, setMood, moodOverlays: MOOD_OVERLAYS, calmMode, toggleCalmMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
